// Messenger.js - with safer initialization
import React, { useEffect, useState } from 'react';
import { Box, Fab, Badge } from '@mui/material';
import { MessageRounded, CloseRounded } from '@mui/icons-material';
import { MessengerProvider, useMessenger } from './MessengerContext';
import ContactPanel from './ContactPanel';
import ChatWindow from './ChatWindow';
import { therapistClient } from '../../../../PatientApps/apiClient'; 

// Main Messenger component wrapper
const Messenger = () => {
  // Create a default currentUser object with a valid uuid
  const defaultUser = {
    uuid: localStorage.getItem('userUuid') || 'placeholder-uuid',
    role: 'therapist'
  };
  
  return (
    <MessengerProvider currentUser={defaultUser} initialChats={[]}>
      <MessengerWithData />
    </MessengerProvider>
  );
};

// Component that sits inside the context and can fetch data
const MessengerWithData = () => {
  const { setAllChats, setLoading } = useMessenger();
  const [clientList, setClientList] = useState([]);
  const [preloadedMessages, setPreloadedMessages] = useState({});
  
  // Fetch chats when component mounts
  useEffect(() => {
    const fetchChats = async () => {
      setLoading(true);
      
      try {
        console.log('Pre-fetching chat data on messenger load...');
        
        // Fetch users with chat info from API
        const response = await therapistClient.get('/api/protected/therapist/chat-users');
        
        if (response && response.data) {
          console.log('Chat data pre-loaded successfully:', response.data);
          
          // Transform API response to format expected by UI
          const clients = response.data.map(client => ({
            id: client.id,
            patient_uuid: client.id,
            uuid: client.id,
            name: client.name || 'Client',
            role: 'client',
            isOnline: client.is_online || false,
            avatar: client.avatar || null,
            chat_uuid: client.chat_uuid || null
          }));

          // Filter out demo accounts in production
          const demoNames = ['James Wilson', 'Sarah Johnson'];
          const filteredClients = process.env.NODE_ENV === 'production' 
            ? clients.filter(client => !demoNames.includes(client.name))
            : clients;
          
          // Store the client list for passing to ContactPanel
          setClientList(filteredClients);
          
          // Collect chat UUIDs from clients who have them
          const chatUuids = filteredClients
            .filter(client => client.chat_uuid)
            .map(client => client.chat_uuid);
          
          // Fetch messages for all chats in parallel
          const messagesMap = {};
          await Promise.all(
            chatUuids.map(async (chatUuid) => {
              try {
                console.log(`Pre-fetching messages for chat: ${chatUuid}`);
                const msgResponse = await therapistClient.get(`/api/protected/therapist/chats/${chatUuid}`);
                if (msgResponse && msgResponse.data && msgResponse.data.messages) {
                  messagesMap[chatUuid] = msgResponse.data.messages;
                  console.log(`Loaded ${msgResponse.data.messages.length} messages for chat ${chatUuid}`);
                }
              } catch (err) {
                console.error(`Error fetching messages for chat ${chatUuid}:`, err);
                // Continue with other chats even if one fails
                messagesMap[chatUuid] = [];
              }
            })
          );
          
          setPreloadedMessages(messagesMap);
          
          // Create chat objects from contacts with chat_uuid and include preloaded messages
          const chats = filteredClients
            .filter(client => client.chat_uuid)
            .map(client => ({
              id: client.chat_uuid,
              chat_uuid: client.chat_uuid,
              participants: [client],
              messages: messagesMap[client.chat_uuid] || [], // Use preloaded messages if available
              unreadCount: 0, // Will be calculated below if messages exist
              name: client.name,
              isGroup: false
            }));
          
          // Update state with chats
          setAllChats(chats);
        }
      } catch (error) {
        console.error('Error pre-loading chats:', error);
        // Don't set error state, just log
      } finally {
        setLoading(false);
      }
    };
    
    fetchChats();
  }, [setAllChats, setLoading]);
  
  return <MessengerUI preloadedContacts={clientList} preloadedMessages={preloadedMessages} />;
};

// UI component that uses the messenger context
const MessengerUI = ({ preloadedContacts, preloadedMessages }) => {
  const { 
    isMessengerOpen, 
    toggleMessenger, 
    activeChats, 
    allChats,
    unreadCount,
    loading 
  } = useMessenger();
  
  return (
    <>
      {/* Messenger button */}
      <Box sx={{ position: 'fixed', bottom: 24, right: 24, zIndex: 1500 }}>
        <Badge
          color="error"
          badgeContent={unreadCount > 9 ? '9+' : unreadCount}
          invisible={unreadCount === 0}
        >
          <Fab
            color="primary"
            onClick={toggleMessenger}
            sx={{ width: 56, height: 56, boxShadow: 6 }}
          >
            {isMessengerOpen ? <CloseRounded /> : <MessageRounded />}
          </Fab>
        </Badge>
      </Box>
      
      {/* Contact panel */}
      {isMessengerOpen && (
        <ContactPanel 
          toggleMessenger={toggleMessenger}
          preloadedContacts={preloadedContacts}
          preloadedMessages={preloadedMessages}
        />
      )}
      
      {/* Active chat windows */}
      {activeChats.map((chatId, index) => {
        const chat = allChats.find(c => c.id === chatId || c.chat_uuid === chatId);
        if (!chat) return null;
        
        return (
          <ChatWindow 
            key={chatId}
            chat={chat}
            position={index}
            preloadedMessages={preloadedMessages && preloadedMessages[chatId]}
          />
        );
      })}
    </>
  );
};

export default Messenger;